/**
 * Engine configuration schemas
 */

import { z } from "zod";

/**
 * WebGL engine configuration
 */
export const WebGLEngineConfigSchema = z.object({
  alpha: z.boolean().optional(),
  antialias: z.boolean().optional(),
  audioEngine: z.boolean().optional(),
  depth: z.boolean().optional(),
  deterministicLockstep: z.boolean().optional(),
  doNotHandleContextLost: z.boolean().optional(),
  failIfMajorPerformanceCaveat: z.boolean().optional(),
  lockstepMaxSteps: z.number().min(1).optional(),
  powerPreference: z
    .enum(["default", "low-power", "high-performance"])
    .optional(),
  premultipliedAlpha: z.boolean().optional(),
  preserveDrawingBuffer: z.boolean().optional(),
  stencil: z.boolean().optional(),
  type: z.literal("webgl"),
  useHighPrecisionFloats: z.boolean().optional(),
  useHighPrecisionMatrix: z.boolean().optional(),
  xrCompatible: z.boolean().optional(),
});

/**
 * WebGPU engine configuration
 */
export const WebGPUEngineConfigSchema = z.object({
  antialias: z.boolean().optional(),
  enableGPUDebugMarkers: z.boolean().optional(),
  glslangUrl: z.string().optional(),
  powerPreference: z.enum(["low-power", "high-performance"]).optional(),
  stencil: z.boolean().optional(),
  twgslUrl: z.string().optional(),
  type: z.literal("webgpu"),
});

/**
 * Null engine configuration (for testing)
 */
export const NullEngineConfigSchema = z.object({
  deterministicLockstep: z.boolean().optional(),
  lockstepMaxSteps: z.number().min(1).optional(),
  renderHeight: z.number().positive().optional(),
  renderWidth: z.number().positive().optional(),
  textureSize: z.number().positive().optional(),
  type: z.literal("null"),
});

/**
 * Engine configuration discriminated union
 */
export const EngineConfigSchema = z.discriminatedUnion("type", [
  WebGLEngineConfigSchema,
  WebGPUEngineConfigSchema,
  NullEngineConfigSchema,
]);

/**
 * Inferred types
 */
export type WebGLEngineConfig = z.infer<typeof WebGLEngineConfigSchema>;
export type WebGPUEngineConfig = z.infer<typeof WebGPUEngineConfigSchema>;
export type NullEngineConfig = z.infer<typeof NullEngineConfigSchema>;
export type EngineConfig = z.infer<typeof EngineConfigSchema>;

/**
 * Type guards
 */
export function isWebGLEngineConfig(
  config: EngineConfig,
): config is WebGLEngineConfig {
  return config.type === "webgl";
}

export function isWebGPUEngineConfig(
  config: EngineConfig,
): config is WebGPUEngineConfig {
  return config.type === "webgpu";
}

export function isNullEngineConfig(
  config: EngineConfig,
): config is NullEngineConfig {
  return config.type === "null";
}

/**
 * Default configurations (separate from schemas as per CLAUDE.md)
 */
export const defaultWebGLEngineConfig: WebGLEngineConfig = {
  alpha: false,
  antialias: true,
  audioEngine: true,
  depth: true,
  deterministicLockstep: false,
  doNotHandleContextLost: false,
  failIfMajorPerformanceCaveat: false,
  lockstepMaxSteps: 4,
  powerPreference: "default",
  premultipliedAlpha: true,
  preserveDrawingBuffer: false,
  stencil: true,
  type: "webgl",
  useHighPrecisionFloats: true,
  useHighPrecisionMatrix: false,
  xrCompatible: false,
};

export const defaultWebGPUEngineConfig: WebGPUEngineConfig = {
  antialias: true,
  enableGPUDebugMarkers: false,
  powerPreference: "high-performance",
  stencil: true,
  type: "webgpu",
};

export const defaultNullEngineConfig: NullEngineConfig = {
  deterministicLockstep: false,
  lockstepMaxSteps: 4,
  renderHeight: 256,
  renderWidth: 512,
  textureSize: 512,
  type: "null",
};

/**
 * Validation helpers
 */
export function validateEngineConfig(input: unknown): EngineConfig {
  return EngineConfigSchema.parse(input);
}

export function safeValidateEngineConfig(
  input: unknown,
):
  | { success: true; data: EngineConfig }
  | { success: false; error: z.ZodError } {
  const result = EngineConfigSchema.safeParse(input);
  if (result.success) {
    return { data: result.data, success: true };
  }
  return { error: result.error, success: false };
}
